import React, { useState } from "react";
import { Check, Copy } from "lucide-react";

import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import { WORKSPACE_TOOL_PROJECTION_STATUSES } from "../../projectionContract";
import ToolProjectionNotice from "./ToolProjectionNotice";
import { mayRender, resolveToolRenderStatus } from "./toolProjectionGuard";

const SECTION_LABEL_CLASS = "text-[11px] font-semibold uppercase tracking-wide text-indigo-300";

// Kebab-case testid fragment from a human-readable field label (e.g.
// "ZIP / Postal Code" -> "zip-postal-code"), so testids stay clean and
// space/slash-free without hand-maintaining a separate key per field.
function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// Two-tier value emphasis: "primary" fields are the ones an agent scans
// for fastest during a live call (name, DOB, phone, email, state);
// "secondary" fields stay at the original compact weight. Restrained by
// design — color/weight only, no size jump, so the inspector still reads
// as one coherent surface rather than a dashboard of differently-sized
// values.
const VALUE_EMPHASIS_CLASS = {
  primary: "text-slate-100 font-semibold",
  secondary: "text-slate-300",
};

/**
 * Small, borderless copy affordance for a single field value. Muted at
 * rest and never fully hidden (opacity is never driven to 0) so it stays
 * discoverable on touch/no-hover input, while still brightening on hover
 * or focus for pointer users. Copies the raw value only — never the
 * field's label.
 */
function CopyButton({ label, value, className = "" }) {
  const { copied, copy } = useCopyToClipboard();

  return (
    <button
      type="button"
      onClick={() => copy(value)}
      aria-label={`Copy ${label}`}
      data-testid={`workspace-lead-copy-${slugify(label)}`}
      className={`shrink-0 rounded p-1 text-slate-600 transition-colors hover:bg-white/10 hover:text-indigo-300 focus-visible:bg-white/10 focus-visible:text-indigo-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-400 group-hover:text-slate-400 ${className}`}
    >
      {copied ? (
        <Check className="h-3 w-3 text-emerald-400" aria-hidden="true" />
      ) : (
        <Copy className="h-3 w-3" aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * A single compact reference row. Renders nothing when the value is empty
 * so the inspector stays dense — a blank Address 2/Province, for example,
 * does not reserve a visible row (and therefore has no copy control).
 * Fields are always kept independently labeled/copyable — never combined
 * into a synthesized composite string — since agents may need to paste
 * individual values into separate carrier-application fields.
 */
function LeadField({ label, value, emphasis = "secondary", copyable = true }) {
  if (!value) return null;
  return (
    <div className="group flex items-baseline justify-between gap-3 text-xs">
      <dt className="shrink-0 text-slate-500">{label}</dt>
      <dd className="flex min-w-0 flex-1 items-center justify-end gap-1">
        <span className={`truncate ${VALUE_EMPHASIS_CLASS[emphasis]}`}>{value}</span>
        {copyable ? <CopyButton label={label} value={value} /> : null}
      </dd>
    </div>
  );
}

function LeadSection({ title, testId, children }) {
  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <p className={SECTION_LABEL_CLASS}>{title}</p>
      <dl className="flex flex-col gap-1">{children}</dl>
    </div>
  );
}

// Collapsed height for long imported Comments — long enough to preview
// several lines, short enough that a lengthy import comment can't take
// over the whole inspector. Expanding grows in place (no nested scroll
// box); the Agent Toolbar's existing tool-panel region remains the sole
// scroll owner.
const COMMENTS_COLLAPSED_CLASS = "line-clamp-4";

function LeadComments({ value, truncated = false }) {
  const [expanded, setExpanded] = useState(false);

  if (!value) return null;

  return (
    <div className="flex flex-col gap-1" data-testid="workspace-lead-tool-comments">
      <p className={SECTION_LABEL_CLASS}>Comments</p>
      <p
        className={`text-xs leading-relaxed text-slate-300 ${expanded ? "" : COMMENTS_COLLAPSED_CLASS}`}
        data-testid="workspace-lead-comments-text"
      >
        {value}
      </p>
      {/* AW-2C-005. Said in its own element rather than appended to the
          comment text, deliberately: the text above is copyable and is
          read as the customer's own words, so a marker inside it would
          be copied into a carrier application as though the customer
          had written it. Shown only when the provider actually cut the
          value -- an agent who cannot see the rest has to know the rest
          exists. */}
      {truncated ? (
        <p
          className="text-[11px] italic text-amber-300/80"
          data-testid="workspace-lead-comments-truncated"
        >
          Shortened — the full comment is longer than shown.
        </p>
      ) : null}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid="workspace-lead-comments-toggle"
        className="self-start text-[11px] font-medium text-indigo-300 hover:text-indigo-200 focus-visible:outline-none focus-visible:underline"
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </div>
  );
}

/**
 * Compact Agent Toolbar Lead inspector — a reference/copy surface the
 * agent can glance at (and copy individual values from) alongside the
 * Script, not a center-canvas editing form. Read-only, and as of AW-2C-005 fed from
 * the owned call's provider-backed customer projection rather than a
 * fixture — no fetch here, no persistence, no edit, no save. Copying an
 * existing value to the clipboard, and expanding a long comment, are
 * the only interactions this surface performs.
 *
 * Those two interactions are why this panel is NOT mounted inside the
 * `<fieldset disabled>` that makes Discovery and Quote read-only: a
 * disabled fieldset disables every descendant button, which would make
 * the copy controls and the comments toggle inert. It needs no such
 * wrapper — it has no editable control to begin with.
 *
 * Deliberately receives its customerInformation/address/leadInformation
 * data as props (defaulted from the fixture) rather than only reading the
 * fixture module internally, matching the seam already established by its
 * DiscoveryPanel/QuotePanel/CalendarToolPanel/NotesPanel siblings, so a
 * later Boundary-A-authorized real selector/hook can supply this shape
 * without redesigning this presentation — see WORKSPACE-AUDIT-007 §13
 * item 3.
 */
export default function LeadInspectorPanel({
  customerInformation,
  address,
  leadInformation,
  projectionStatus,
  commentsTruncated = false,
}) {
  // AW-ORIGINAL-RESTORE-001. No fixture defaults and no implicit `ok`:
  // the projection is required, and a payload missing any section is
  // withheld whole rather than rendered with blanks an agent would read
  // as "this prospect has none".
  const renderStatus = resolveToolRenderStatus(projectionStatus, [
    customerInformation,
    address,
    leadInformation,
  ]);

  if (!mayRender(renderStatus)) {
    return (
      <ToolProjectionNotice
        status={renderStatus.status}
        reason={renderStatus.reason}
        testIdPrefix="workspace-lead"
      />
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="workspace-lead-tool-panel">
      <LeadSection title="Contact" testId="workspace-lead-tool-customer-information">
        <LeadField label="First Name" value={customerInformation.firstName} emphasis="primary" />
        <LeadField label="MI" value={customerInformation.middleInitial} />
        <LeadField label="Last Name" value={customerInformation.lastName} emphasis="primary" />
        <LeadField label="Gender" value={customerInformation.gender} />
        <LeadField label="DOB" value={customerInformation.dob} emphasis="primary" />
        <LeadField label="Phone" value={customerInformation.phone} emphasis="primary" />
        <LeadField label="Alternate Phone" value={customerInformation.alternatePhone} />
        <LeadField label="Email" value={customerInformation.email} emphasis="primary" />
      </LeadSection>

      <LeadSection title="Address" testId="workspace-lead-tool-address">
        <LeadField label="Address 1" value={address.address1} />
        <LeadField label="Address 2" value={address.address2} />
        <LeadField label="Address 3" value={address.address3} />
        <LeadField label="City" value={address.city} />
        <LeadField label="State" value={address.state} emphasis="primary" />
        <LeadField label="ZIP / Postal Code" value={address.postalCode} />
        <LeadField label="Province" value={address.province} />
      </LeadSection>

      <LeadSection title="Lead" testId="workspace-lead-tool-lead-information">
        <LeadField label="Vendor ID" value={leadInformation.vendorId} copyable={false} />
        <LeadField label="Lead ID" value={leadInformation.leadId} copyable={false} />
      </LeadSection>

      <LeadComments value={leadInformation.comments} truncated={commentsTruncated} />
    </div>
  );
}
